// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { catchError, map, mergeMap, filter } from "rxjs/operators"
import { Action } from "redux"
import { DeleteTagKeywordRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { TagKeyword, TagKeywordSchema } from "../../../generated/legebill/type/tag_pb"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"

type DeleteTagKeyword = (request: Init<typeof DeleteTagKeywordRequestSchema>) => Observable<Empty>

export const deleteTagKeyword = (deleteTagKeywordRpc: DeleteTagKeyword) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTagKeyword.match),
      mergeMap(({ payload }) => {
        if (!payload.keyword) {
          return EMPTY
        }
        const keyword = create(TagKeywordSchema, payload.keyword)
        return deleteTagKeywordRpc(payload).pipe(
          map(_ => Slice.deleteTagKeywordFulfilled(keyword)),
          catchError(e => handleError(e, keyword)),
        )
      })
    )

const handleError = (e: unknown, tagKeyword: TagKeyword): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_delete_tag_keyword_not_found", message: "Tag not found. Unable to delete the keyword." }),
          messageType: "ERROR",
        })),
        of(Slice.setDialogStatus("CLOSED")),
        of(Slice.removeFromCache(tagKeyword.tagId))
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_delete_tag_keyword_permission_denied", message: "Failed to delete keyword. You do not have permission to edit tags." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_delete_tag_keyword_failed", message: "Failed to delete keyword. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const deleteTagKeywordRpc = (request: Init<typeof DeleteTagKeywordRequestSchema>): Observable<Empty> =>
  executorMutation(() => tagService.deleteTagKeyword(create(DeleteTagKeywordRequestSchema, request)))

export default deleteTagKeyword(deleteTagKeywordRpc)
